<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" sm="4" lg="2" class="py-2 my-0">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="displayDate"
              readonly
              outlined
              label="Date Range"
              dense
              v-bind="attrs"
              v-on="on"
              hide-details="auto"
            ></v-text-field>
          </template>
          <v-sheet
            class="pa-4"
            elevation="4"
            style="background-color: white; border-radius: 8px"
          >
            <v-row no-gutters>
              <v-col>
                <v-date-picker
                  v-model="selectedDates"
                  range
                  @input="onDateChange"
                ></v-date-picker>
              </v-col>
              <v-col class="pl-4 pt-4" style="max-width: 150px">
                <div
                  v-for="option in dateOptions"
                  :key="option.label"
                  class="mb-2 pt-2"
                >
                  <v-btn small color="accent" @click="setQuickDate(option)">
                    {{ option.label }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-sheet>
        </v-menu>
      </v-col>

      <v-col cols="12" md="4" sm="4" lg="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.types"
          :items="feeTypes"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Particular *"
          item-text="text"
          item-value="id"
          multiple
          :error="validationErrors.particular"
          :error-messages="
            validationErrors.particular ? 'Particular is required' : ''
          "
          @change="clearValidationError('particular')"
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="index === 0">
              <v-chip small>{{ item.text }}</v-chip>
            </span>
            <span
              style="font-size: 10px"
              v-if="index === 1 && form.types.length > 1"
            >
              &nbsp;+{{ form.types.length - 1 }} others
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" md="3" sm="4" lg="2">
        <v-select
          v-model="form.year_batch"
          hide-details="auto"
          :items="batches"
          label="
          Batch"
          outlined
          dense
          clearable
        ></v-select>
      </v-col>

      <v-col cols="12" md="3" sm="4" lg="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme *"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          :error="validationErrors.program"
          :error-messages="
            validationErrors.program ? 'Programme is required' : ''
          "
          @change="clearValidationError('program')"
        ></v-autocomplete>
      </v-col>

      <v-col
        cols="12"
        md="3"
        sm="4"
        lg="2"
        class="py-0 my-0"
        v-if="showPartner()"
      >
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>

        <v-btn
          class="accent white--text ml-2"
          :loading="downloadLoading"
          @click="downloadAuditReport()"
          :disabled="downloadLoading || !isFormValid"
        >
          Download
        </v-btn>
      </v-col>
    </v-row>

    <div class="pt-6 text-5xl font-weight-bold accent--text">
      {{ getSelectedFeeTypeText() }} Collection Report
      <span
        v-if="form.from_date && form.to_date"
        class="text-5xl font-weight-bold accent--text"
      >
        for the period of
        {{ formatDisplayDate(form.from_date, form.to_date) }}
      </span>
    </div>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card elevation="0">
          <v-card-text class="py-1 px-4">
            <div
              v-if="
                form.program_id &&
                selectedProgramData &&
                selectedProgramData.terms &&
                selectedProgramData.terms.length > 0
              "
            >
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr class="grey lighten-4">
                      <th class="text-left font-weight-bold">Term Number</th>
                      <th class="text-right font-weight-bold">Count</th>
                      <th class="text-right font-weight-bold">Amount (in ₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="term in selectedProgramData.terms"
                      :key="term.term_number"
                    >
                      <td class="text-left">{{ term.term_number }}</td>
                      <td class="text-right">{{ term.count }}</td>
                      <td class="text-right">
                        {{ formatAmount(term.amount) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="font-weight-bold">
                      <td class="text-left">Total</td>
                      <td class="text-right">
                        {{ selectedProgramData.total_count }}
                      </td>
                      <td class="text-right">
                        {{ formatAmount(selectedProgramData.total_amount) }}
                      </td>
                    </tr>
                  </tfoot>
                </template>
              </v-simple-table>
            </div>

            <div v-else-if="allPrograms.length > 0">
              <v-row class="d-flex justify-center">
                <v-col
                  v-for="program in allPrograms"
                  :key="program.program_id"
                  cols="12"
                  md="6"
                  lg="4"
                  class="pa-3"
                >
                  <v-card outlined class="h-100 d-flex flex-column">
                    <v-card-text class="pa-0 flex-grow-1">
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Term</th>
                              <th class="text-right">Count</th>
                              <th class="text-right">Amount (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="term in program.terms"
                              :key="term.term_number"
                            >
                              <td class="text-left">{{ term.term_number }}</td>
                              <td class="text-right">{{ term.count }}</td>
                              <td class="text-right">
                                {{ formatAmount(term.amount) }}
                              </td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr class="font-weight-bold">
                              <td class="text-left">Total</td>
                              <td class="text-right">
                                {{ program.total_count }}
                              </td>
                              <td class="text-right">
                                {{ formatAmount(program.total_amount) }}
                              </td>
                            </tr>
                          </tfoot>
                        </template>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <v-divider
              v-if="allPrograms.length > 0 && !form.program_id"
            ></v-divider>

            <v-simple-table v-if="allPrograms.length > 0 && !form.program_id">
              <template v-slot:default>
                <tfoot>
                  <tr class="font-weight-bold accent--text">
                    <td class="text-left">GRAND TOTAL</td>
                    <td class="text-right">{{ grandTotalCount }}</td>
                    <td class="text-right">
                      {{ formatAmount(grandTotalAmount) }}
                    </td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>

            <div v-if="allPrograms.length === 0" class="text-center py-4">
              No data available
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="pt-6 text-5xl font-weight-bold accent--text">
      {{ form.program_id ? getProgramName(form.program_id) : "" }} Collection
      Report List
      <span
        v-if="form.from_date && form.to_date"
        class="text-5xl font-weight-bold accent--text"
      >
        for the period of
        {{ formatDisplayDate(form.from_date, form.to_date) }}
      </span>
    </div>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="students"
            :loading="studentsLoading"
            fixed-header
            item-key="usn_id"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>

            <template v-slot:item.index="{ index }">
              {{ index + 1 }}
            </template>

            <template v-slot:item.usn_id="{ item }">
              <span
                @click="openStudentDialog(item)"
                style="cursor: pointer; text-decoration: underline"
                class="accent--text"
              >
                {{ item.usn_id ? item.usn_id : "-" }}
              </span>
            </template>

            <template v-slot:item.amount="{ item }">
              {{ formatAmount(item.amount) }}
            </template>

            <template v-slot:item.transaction_date="{ item }">
              {{ formatDate(item.transaction_date) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="studentDialog" width="800">
      <v-card v-if="selectedStudent">
        <v-card-title class="primary white--text">
          Student Details - {{ selectedStudent.usn_id }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="studentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="mt-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      USN
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.usn_id ? selectedStudent.usn_id : "-" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Name
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Email
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Mobile Number
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.mobile_no }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Amount Paid
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatAmount(selectedStudent.amount) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Receipt Number
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.receipt_number }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedStudent.transaction_date">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Transaction Date
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatDate(selectedStudent.transaction_date) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedStudent.type">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      Particular
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ selectedStudent.type }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" @click="studentDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { auditReportHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      menu: false,
      selectedDates: [],
      displayDate: "",
      form: {
        from_date: "",
        to_date: "",
        types: [],
        program_id: null,
        partner_id: null,
        current_term_no: null,
        year_batch: null,
      },
      programs: [],
      partners: [],
      feeTypes: [
        { id: "Advance Tuition Fee", text: "Advance Tuition Fee" },
        { id: "Tuition Fee", text: "Tuition Fee" },
        { id: "Exam Fee", text: "Exam Fee" },
        { id: "Re-Admission Fee", text: "Re-Admission Fee" },
        { id: "Convocation Fee", text: "Convocation Fee" },
        { id: "Postal Fee", text: "Postal Fee" },
        { id: "Add-on Programme", text: "Add-on Programme" },
      ],
      dateOptions: [
        { label: "Today", days: 0 },
        { label: "Yesterday", type: "yesterday" },
        { label: "This Week", type: "week" },
        { label: "Last 7 Days", days: 6 },
        { label: "This Month", type: "month" },
        { label: "Last 30 Days", days: 29 },
        { label: "Reset", type: "reset" },
      ],
      rules,
      loading: false,
      downloadLoading: false,
      studentsLoading: false,
      summaryLoading: false,
      students: [],
      paymentSummary: {
        programs: [],
        summary: {
          total_count: 0,
          total_amount: 0,
          terms: [],
        },
      },
      headers: auditReportHeaders,
      selectedStudent: null,
      studentDialog: false,
      validationErrors: {
        particular: false,
        program: false,
      },
      showValidationAlert: false,
      batches: [],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    allPrograms() {
      return this.paymentSummary.programs || [];
    },

    selectedProgramData() {
      if (!this.form.program_id) return null;
      return this.allPrograms.find(
        (program) => program.program_id === this.form.program_id,
      );
    },

    grandTotalCount() {
      return this.allPrograms.reduce(
        (sum, program) => sum + program.total_count,
        0,
      );
    },

    grandTotalAmount() {
      return this.allPrograms.reduce(
        (sum, program) => sum + parseInt(program.total_amount || 0, 10),
        0,
      );
    },

    isFormValid() {
      return (
        Array.isArray(this.form.types) &&
        this.form.types.length > 0 &&
        !!this.form.program_id
      );
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Audit Report" }]);
    await this.getPrograms();
    await this.showPartnerFilter();
    await this.generateBatches();
  },

  methods: {
    async generateBatches() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data || [];
      } catch (error) {
        console.log(error);
      }
    },

    getSelectedFeeTypeText() {
      if (!Array.isArray(this.form.types) || this.form.types.length === 0) {
        return "";
      }

      return this.feeTypes
        .filter((f) => this.form.types.includes(f.id))
        .map((f) => f.text)
        .join(", ");
    },

    validateForm() {
      let isValid = true;

      if (!Array.isArray(this.form.types) || this.form.types.length === 0) {
        this.validationErrors.particular = true;
        isValid = false;
      } else {
        this.validationErrors.particular = false;
      }

      if (!this.form.program_id) {
        this.validationErrors.program = true;
        isValid = false;
      } else {
        this.validationErrors.program = false;
      }

      if (!isValid) {
        this.showValidationAlert = true;
        setTimeout(() => {
          this.showValidationAlert = false;
        }, 5000);
      }

      return isValid;
    },

    clearValidationError(field) {
      this.validationErrors[field] = false;
      if (!this.validationErrors.particular && !this.validationErrors.program) {
        this.showValidationAlert = false;
      }
    },

    async getTuitionFeesReport() {
      if (!this.validateForm()) return;

      this.loading = true;
      this.studentsLoading = true;
      this.summaryLoading = true;

      try {
        const queryParams = {
          from_date: this.form.from_date,
          to_date: this.form.to_date,
          fee_types: this.form.types,
          program_id: this.form.program_id,
          partner_id: this.form.partner_id,
          term_no: this.form.current_term_no,
          year_batch: this.form.year_batch,
        };

        const queryString = this.objectToQueryParams(queryParams);

        const response = await this.$axios.get(
          `/tuition-fees-collection?${queryString}`,
        );

        if (response.data.success) {
          this.paymentSummary = response.data.data.summary;
          this.students = response.data.data.students || [];
          this.students = this.students.sort((a, b) => {
            return new Date(b.transaction_date) - new Date(a.transaction_date);
          });
        }
      } catch (error) {
        console.error("Error fetching tuition fees report:", error);
        this.$toast.error("Failed to load report data");
      } finally {
        this.loading = false;
        this.studentsLoading = false;
        this.summaryLoading = false;
      }
    },

    async getPrograms() {
      let endpoint = "/programs";
      if (this.currentUser.partner_id != 1) {
        endpoint = `/get_partner_programs/${this.currentUser.partner_id}`;
      }

      try {
        const response = await this.$axios.get(endpoint);
        this.programs = (response.data.data || []).sort((a, b) =>
          a.degree?.short_name.localeCompare(b.degree?.short_name),
        );
      } catch (error) {
        console.log(error);
      }
    },

    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data || [];
      } catch (error) {
        console.log(error);
      }
    },

    async showPartnerFilter() {
      if (this.currentUser.partner_id != 1) {
        this.form.partner_id = this.currentUser.partner_id;
      }

      await this.getPartners();

      if (
        this.currentUser.role === "Partner" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id,
        );
      }
    },

    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " " + program.specialization : "") +
            " - " +
            program.degree.long_name
        : "";
    },

    getProgramLongName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program ? program.degree.short_name : "";
    },

    showPartner() {
      return (
        this.currentUser.partner_id == 1 ||
        (this.currentUser.role === "Partner" &&
          this.partners.length > 1 &&
          this.currentUser.partner_level != "S")
      );
    },

    init() {
      this.getTuitionFeesReport();
    },

    downloadAuditReport() {
      if (!this.validateForm()) return;

      this.downloadLoading = true;

      const queryParams = {
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        fee_types: this.form.types,
        program_id: this.form.program_id,
        partner_id: this.form.partner_id,
        term_no: this.form.current_term_no,
        year_batch: this.form.year_batch,
      };

      const queryString = this.objectToQueryParams(queryParams);

      this.$axios
        .get(`/download_audit_report?${queryString}`, {
          responseType: "blob",
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Audit_Report_${new Date().toISOString().split("T")[0]}.csv`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.$toast.success("Download started successfully");
        })
        .catch((error) => {
          console.error("Error downloading report:", error);
          this.$toast.error("Failed to download report");
        })
        .finally(() => {
          this.downloadLoading = false;
        });
    },

    openStudentDialog(item) {
      this.selectedStudent = item;
      this.studentDialog = true;
    },

    formatAmount(amount) {
      if (!amount) return "0";
      const numAmount = typeof amount === "object" ? Number(amount) : amount;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numAmount);
    },

    formatDate(date) {
      if (!date) return "";
      const newDate = new Date(date);
      const day = String(newDate.getDate()).padStart(2, "0");
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const year = newDate.getFullYear();
      return `${day}-${month}-${year}`;
    },

    formatDateBackend(date) {
      const newDate = new Date(date);
      const day = String(newDate.getDate()).padStart(2, "0");
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const year = newDate.getFullYear();
      return `${year}-${month}-${day}`;
    },

    formatDisplayDate(from, to) {
      return `${this.formatDate(from)}~${this.formatDate(to)}`;
    },

    objectToQueryParams(obj) {
      const queryObj = { ...obj };

      if (queryObj.year_batch) {
        const [year, batch] = String(queryObj.year_batch).split("-");
        queryObj.year = year;
        queryObj.batch = batch;
        delete queryObj.year_batch;
      }

      return Object.keys(queryObj)
        .filter(
          (key) =>
            queryObj[key] !== undefined &&
            queryObj[key] !== null &&
            queryObj[key] !== "" &&
            !(Array.isArray(queryObj[key]) && queryObj[key].length === 0),
        )
        .map((key) => {
          const value = Array.isArray(queryObj[key])
            ? queryObj[key].join(",")
            : queryObj[key];
          return (
            encodeURIComponent(key) + "=" + encodeURIComponent(value)
          );
        })
        .join("&");
    },

    onDateChange() {
      if (this.selectedDates.length === 2) {
        const [fromDate, toDate] = this.selectedDates;
        this.form.from_date = this.formatDateBackend(fromDate);
        this.form.to_date = this.formatDateBackend(toDate);
        this.displayDate = this.formatDisplayDate(fromDate, toDate);
      }
    },

    setQuickDate(option) {
      if (option.type === "reset") {
        this.form.from_date = null;
        this.form.to_date = null;
        this.displayDate = "";
        this.selectedDates = [];
        return;
      }

      const today = new Date();
      const fromDate = new Date(today);
      const toDate = new Date(today);

      if (option.type === "week") {
        fromDate.setDate(today.getDate() - today.getDay());
      } else if (option.type === "month") {
        fromDate.setDate(1);
      } else if (option.type === "yesterday") {
        fromDate.setDate(today.getDate() - 1);
        toDate.setDate(today.getDate() - 1);
      } else if (option.days !== undefined) {
        fromDate.setDate(today.getDate() - option.days);
      }

      this.selectedDates = [
        this.formatDateBackend(fromDate),
        this.formatDateBackend(toDate),
      ];

      this.onDateChange();
    },
  },
};
</script>

<style scoped>
.v-simple-table thead tr th {
  font-weight: bold;
  background-color: #f5f5f5;
}

.h-100 {
  height: 100%;
}

.v-card {
  transition: all 0.2s ease;
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@media (max-width: 960px) {
  .v-card-text {
    overflow-x: auto;
  }

  .v-simple-table {
    min-width: 300px;
  }
}
</style>
